import { INIT_FILTER_VALUES } from "../../constants";
import { convertUTCDateToLocalDate } from "../../../../utils";

export const MAP_EXPLORER_MODE = "map-explorer";
export const GRAPH_MODE = "graph-mode";

const allValues = (options) => (options || []).map(({ value }) => value);

/**
 * Plain (non filter-config) default values for each mode
 * Explorer shows every location, graph mode hides locations without records
 * @param {string} mode "map-explorer" | "graph-mode"
 * @param {object} filtersData response from /api/public-map/filters
 */
export const getModeDefaults = (mode, filtersData) => ({
  locationTypes: allValues(filtersData?.locationTypes),
  parameterGroups: allValues(filtersData?.parameterGroups),
  parameters: allValues(filtersData?.parameters),
  hasTelem: INIT_FILTER_VALUES.hasTelem.value,
  isActive: INIT_FILTER_VALUES.isActive.value,
  isCcbwqa: INIT_FILTER_VALUES.isCcbwqa.value,
  flow: "alldata",
  analysis: "median",
  minRecords: mode === GRAPH_MODE ? 1 : 0,
});

/**
 * The flow selection controls which layer fields the parameters and
 * min records filters are applied against
 * This is the only place those field names should be derived
 */
export const applyFlow = (filterValues, flow) => ({
  ...filterValues,
  flow: { ...filterValues.flow, value: flow },
  parameters: {
    ...filterValues.parameters,
    layerFieldName: `parameterid_array_${flow}`,
  },
  min_records: {
    ...filterValues.min_records,
    layerFieldName: `recordcount_${flow}`,
  },
});

/**
 * Builds the full filter state (options + values) from plain values
 * @param {object} filtersData response from /api/public-map/filters
 * @param {object} values plain values in the shape returned by getModeDefaults
 */
export const buildFilterValues = (filtersData, values) => {
  const withOptions = (key, options) => ({
    ...INIT_FILTER_VALUES[key],
    options: options || [],
  });

  return applyFlow(
    {
      ...INIT_FILTER_VALUES,
      locationTypes: {
        ...withOptions("locationTypes", filtersData?.locationTypes),
        value: values.locationTypes,
      },
      parameterGroups: {
        ...withOptions("parameterGroups", filtersData?.parameterGroups),
        value: values.parameterGroups,
      },
      parameters: {
        ...withOptions("parameters", filtersData?.parameters),
        value: values.parameters,
      },
      analysis: {
        ...withOptions("analysis", filtersData?.analysis),
        value: values.analysis,
      },
      flow: {
        ...withOptions("flow", filtersData?.flow),
        value: values.flow,
      },
      min_records: {
        ...INIT_FILTER_VALUES.min_records,
        value: values.minRecords,
      },
      hasTelem: { ...INIT_FILTER_VALUES.hasTelem, value: values.hasTelem },
      isActive: { ...INIT_FILTER_VALUES.isActive, value: values.isActive },
      isCcbwqa: { ...INIT_FILTER_VALUES.isCcbwqa, value: values.isCcbwqa },
      startDate: {
        ...INIT_FILTER_VALUES.startDate,
        value: filtersData?.startDate
          ? convertUTCDateToLocalDate(new Date(filtersData.startDate))
          : null,
      },
      endDate: {
        ...INIT_FILTER_VALUES.endDate,
        value: filtersData?.endDate
          ? convertUTCDateToLocalDate(new Date(filtersData.endDate))
          : null,
      },
    },
    values.flow
  );
};

/**
 * Extracts the plain values from the full filter state
 * Inverse of buildFilterValues (dates excluded, they are server state)
 */
export const getPlainFilterValues = (filterValues) => ({
  locationTypes: filterValues.locationTypes.value,
  parameterGroups: filterValues.parameterGroups.value,
  parameters: filterValues.parameters.value,
  hasTelem: filterValues.hasTelem.value,
  isActive: filterValues.isActive.value,
  isCcbwqa: filterValues.isCcbwqa.value,
  flow: filterValues.flow.value,
  analysis: filterValues.analysis.value,
  minRecords: filterValues.min_records.value,
});

/**
 * In graph mode, parameters must belong to a selected parameter group
 * @param {Array} parameters selected parameter ids
 * @param {Array} parameterGroups selected parameter group names
 * @param {Array} parameterOptions filtersData.parameters
 */
export const keepParametersInGroups = (
  parameters,
  parameterGroups,
  parameterOptions
) => {
  const groupByParameter = new Map(
    (parameterOptions || []).map((option) => [option.value, option.group])
  );
  return parameters.filter((parameterId) =>
    parameterGroups.includes(groupByParameter.get(parameterId))
  );
};
